import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { generatePath, Link } from 'react-router'
import { type Components, Virtuoso } from 'react-virtuoso'
import { TruncatedText } from '@/components/commons/truncated-text'
import { Skeleton } from '@/components/ui/skeleton'
import { formatDate } from '@/dates'
import { EventRowMenu } from '@/features/marquee/components/event-row-menu'
import { EventStatusBadge } from '@/features/marquee/components/event-status-badge'
import {
	TableEmpty,
	TableError
} from '@/features/marquee/components/table-states'
import { eventListQueryOptions } from '@/features/marquee/query-options'
import {
	eventMatchesQuery,
	eventPlaceName,
	eventTypeLabelKey
} from '@/features/marquee/utils'
import { cn } from '@/helpers/utils'
import { routes } from '@/routes'
import type { MarqueeEventListItem, MarqueeEventState } from '@/sdk'
import { useTranslation } from '@/translations/i18n'

const ITEM_CLASS = 'px-4 l:px-10 pb-6 first:pt-10 last:pb-10'
const CARD_CLASS = 'rounded-2xl bg-neutral-white p-4 shadow-airbnb-3 xl:p-6'
const CARD_GRID_CLASS =
	'grid grid-cols-1 gap-4 l:grid-cols-2 xl:grid-cols-[2fr_1fr_1fr] xl:items-center xl:gap-6'

const ListItem: Components<MarqueeEventListItem>['Item'] = ({
	children,
	context: _context,
	item: _item,
	...props
}) => (
	<div className={ITEM_CLASS} {...props}>
		{children}
	</div>
)

function EventCard({ event }: { event: MarqueeEventListItem }) {
	const { t } = useTranslation()
	// Once the desk has opened, attendance replaces the invitee count: it is what
	// the operator is tracking from that point on.
	const countLabel = (() => {
		if (event.state === 'live' || event.state === 'closed') {
			return t('marquee:events_attended', {
				count: event.checked_in ?? 0
			})
		}
		return event.invited
			? t('marquee:events_invited', { count: event.invited })
			: t('marquee:events_not_imported')
	})()
	return (
		<Link
			className={cn(CARD_CLASS, 'flex items-center gap-2')}
			to={generatePath(routes.MARQUEE_EVENT_DETAIL.handle.fullPath, {
				id: event.id
			})}
		>
			<div className={cn(CARD_GRID_CLASS, 'min-w-0 flex-1')}>
				<div className='l:col-span-2 flex min-w-0 flex-col gap-2 xl:col-span-1'>
					<TruncatedText className='font-medium text-base text-neutral-900'>
						{event.name}
					</TruncatedText>
					<TruncatedText className='text-base text-neutral-500'>
						{[
							formatDate(event.date, 'PP'),
							eventPlaceName(event),
							event.format,
							// In-person / virtual, or "not recorded" for events
							// created before the field existed — never rendered as
							// in-person by omission.
							t(eventTypeLabelKey(event.event_type)),
							event.hashtag
						]
							.filter(Boolean)
							.join(' · ')}
					</TruncatedText>
				</div>

				<EventStatusBadge
					className='justify-self-start'
					event={event}
				/>

				<div className='flex items-center gap-1 justify-self-start l:justify-self-end whitespace-nowrap text-sm'>
					<span className='font-medium text-neutral-900'>
						{countLabel}
					</span>
				</div>
			</div>

			{/* Inside the link, like every other part of the card: the kebab
			    is what refuses the navigation, not the layout. */}
			<EventRowMenu event={event} />
		</Link>
	)
}

function EventCardSkeleton() {
	return (
		<div className={cn(CARD_CLASS, CARD_GRID_CLASS)}>
			<div className='l:col-span-2 flex flex-col gap-2 xl:col-span-1'>
				<Skeleton height={20} width={220} />
				<Skeleton height={18} width={300} />
			</div>
			<div className='justify-self-start'>
				<Skeleton height={24} width={110} />
			</div>
			<div className='justify-self-start l:justify-self-end'>
				<Skeleton height={18} width={160} />
			</div>
		</div>
	)
}

const SKELETON_KEYS = ['s1', 's2', 's3', 's4', 's5']

interface EventsListProps {
	statuses?: MarqueeEventState[]
	search?: string
	/**
	 * When true (mobile), Virtuoso virtualizes against the document/window scroll
	 * so the list flows with the page body. On desktop it keeps its own
	 * fixed-height internal scroll.
	 */
	useWindowScroll?: boolean
}

export function EventsList({
	statuses,
	search,
	useWindowScroll
}: EventsListProps) {
	const { data, isLoading, isError } = useQuery(eventListQueryOptions())

	// Nothing is paginated, so the whole list is already here: both the tab
	// filter and the search run on it, and neither costs a request.
	const events = useMemo(() => {
		const query = search?.trim().toLowerCase()
		return (data?.items ?? []).filter(
			(event) =>
				(!statuses?.length || statuses.includes(event.state)) &&
				(!query || eventMatchesQuery(event, query))
		)
	}, [data, search, statuses])

	if (isLoading) {
		return (
			<div>
				{SKELETON_KEYS.map((key) => (
					<div className={ITEM_CLASS} key={key}>
						<EventCardSkeleton />
					</div>
				))}
			</div>
		)
	}

	if (isError) {
		return (
			<TableError
				className='flex-1'
				description='marquee:error_description'
				title='marquee:error_title'
			/>
		)
	}

	if (events.length === 0) {
		return (
			<TableEmpty
				className='flex-1'
				description='marquee:empty_description'
				title='marquee:no_events'
			/>
		)
	}

	return (
		<Virtuoso
			className={useWindowScroll ? undefined : 'min-h-0 flex-1'}
			components={{ Item: ListItem }}
			data={events}
			itemContent={(_, event) => <EventCard event={event} />}
			overscan={400}
			useWindowScroll={useWindowScroll}
		/>
	)
}
